import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';

import { areEqual } from 'Utils/equalityChecks';
import { Gallery } from 'Components/RocketScan';

import { listRoomPhotosPerAlbum } from 'Containers/RocketScan/RoomsView/RoomContent/Gallery/actions';
import { refreshDataForPhotoSelection, setPhotosDeleted } from 'Containers/RocketScan/Header/ActionsCenter/actions';

import { generateUUID, getPhotosCount } from 'Utils/helpers';
import { useOnScreen } from 'Hooks/useOnScreen';
import { locationSelector } from 'Containers/RocketScan/MultiUnit/Locations/selectors';
import { listLocationRoomsForPhotoSelect } from 'Containers/RocketScan/RoomsView/Rooms/actions';
import { pusherSelector } from 'Containers/Core/selectors';
import { userFeatureFlagsSelector } from 'Containers/User/selector';
import { useRoomFunctions } from 'Context/RocketScan';
import type { PhotoModal } from 'Containers/PhotoViewCarousel/Models';

const setAlbumAndPhotos = (albums, photosCount, albumId) => {
  const items = [];

  if (albums.length > 0) {
    albums.forEach(({ id, photos }: any) => {
      if (id === albumId) {
        items.push({
          id,
          photos: photosCount,
        });
      } else {
        items.push({
          id,
          photos,
        });
      }
    });
  }

  return items;
};

const PENDING_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

type PendingPhoto = {
  id: string;
  pending: true;
  fileName: string;
  uploadId: string;
  createdAt: number;
};

type GalleryRefreshReason =
  | 'initial_on_screen'
  | 'photo_upload_completed_event'
  | 'image_processor_refresh_token'
  | 'photo_delete';

interface Props {
  title: string;
  roomId: number;
  albumId: number;
  hide?: boolean;
  editable: boolean;
  setAlbumPhotos: (e: any) => void;
}

const GalleryContainer = ({ title, roomId, albumId, hide, editable, setAlbumPhotos }: Props) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const mounted = useRef(true);

  const isOnScreen = useOnScreen(ref);

  // show spinner locally when api is being called
  const [fetching, setFetching] = useState(true);
  const [refreshOnDelete, setRefreshOnDelete] = useState(false);
  // this will make sure the photos API will be called once, not every time user scrolls up down
  const [initialLoad, setInitialLoad] = useState(true);

  // selectors
  const location = useSelector(locationSelector, areEqual);
  const pusher = useSelector(pusherSelector, areEqual);
  const { imageProcessor } = useSelector(userFeatureFlagsSelector, areEqual);
  const { imageProcessorRefreshToken } = useRoomFunctions() as any;

  // local variables
  const [photos, setPhotos] = useState<PhotoModal[]>([]);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const photosRef = useRef<PhotoModal[]>([]);
  const pendingPhotosRef = useRef<PendingPhoto[]>([]);
  const completedUploadIdsRef = useRef<string[]>([]);
  const galleryInstanceIdRef = useRef(generateUUID());
  const inFlightRequestCountRef = useRef(0);
  const requestSequenceRef = useRef(0);
  const lastRefreshMetaRef = useRef<{ reason: GalleryRefreshReason; startedAtMs: number; requestId: number } | null>(null);
  const [internalPhotosCount, setInternalPhotosCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(2);
  const [photosCount, setPhotosCount] = useState(0);

  const addGalleryBreadcrumb = useCallback(
    (message: string, data: Record<string, unknown> = {}, level: Sentry.SeverityLevel = 'info') => {
      Sentry.addBreadcrumb({
        category: 'gallery',
        level,
        message,
        data: {
          galleryInstanceId: galleryInstanceIdRef.current,
          roomId,
          albumId,
          title,
          ...data,
        },
      });
    },
    [albumId, roomId, title]
  );

  // fetchData internally per album as user scrolls down
  const getPhotos = useCallback(async (reason: GalleryRefreshReason) => {
    const startedAtMs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const requestId = requestSequenceRef.current + 1;
    const previousRefresh = lastRefreshMetaRef.current;
    const inFlightBefore = inFlightRequestCountRef.current;

    requestSequenceRef.current = requestId;
    lastRefreshMetaRef.current = { reason, startedAtMs, requestId };
    inFlightRequestCountRef.current += 1;

    addGalleryBreadcrumb('getPhotos start', {
      requestId,
      reason,
      inFlightBefore,
      previousReason: previousRefresh?.reason || null,
      msSincePreviousStart: previousRefresh ? startedAtMs - previousRefresh.startedAtMs : null,
      currentPhotoCount: photosRef.current.length,
    });

    if (inFlightBefore > 0 || (previousRefresh && startedAtMs - previousRefresh.startedAtMs < 1500)) {
      Sentry.captureMessage('Gallery photo refresh overlap detected', {
        level: 'warning',
        tags: {
          feature: 'gallery',
          reason,
        },
        extra: {
          albumId,
          roomId,
          title,
          galleryInstanceId: galleryInstanceIdRef.current,
          requestId,
          inFlightBefore,
          previousReason: previousRefresh?.reason || null,
          msSincePreviousStart: previousRefresh ? startedAtMs - previousRefresh.startedAtMs : null,
          currentPhotoCount: photosRef.current.length,
          pendingPhotoCount: pendingPhotosRef.current.length,
        },
      });
    }

    setFetching(true);

    try {
      const response: any = await dispatch(listRoomPhotosPerAlbum(roomId, albumId));

      if (response?.data) {
        const { data } = response;
        const { meta } = response;
        const { total } = meta;

        const previousPhotos = photosRef.current || [];
        const previousPhotoIds = new Set(previousPhotos.map((photo: PhotoModal) => photo.id));
        const newPhotosCount = data.filter((photo: PhotoModal) => !previousPhotoIds.has(photo.id)).length;

        if (data.length > 0) {
          setPhotosCount(total);
          setInternalPhotosCount(data.length);
          setPhotos(data);

          if (newPhotosCount > 0) {
            setPendingPhotos((prev) => {
              if (prev.length === 0) return prev;

              let remaining = newPhotosCount;
              let nextPending = [...prev];
              const uploadQueue = [...completedUploadIdsRef.current];
              const removeFromUpload = (items: PendingPhoto[], uploadId: string, count: number) => {
                let removed = 0;
                const updated: PendingPhoto[] = [];

                items.forEach((item) => {
                  if (item.uploadId === uploadId && removed < count) {
                    removed += 1;
                    return;
                  }

                  updated.push(item);
                });

                return { updated, removed };
              };

              while (remaining > 0 && uploadQueue.length > 0) {
                const currentUploadId = uploadQueue[0];
                const { updated, removed } = removeFromUpload(nextPending, currentUploadId, remaining);

                if (removed === 0) {
                  uploadQueue.shift();
                  continue;
                }

                remaining -= removed;
                nextPending = updated;

                const hasMoreFromUpload = nextPending.some((photo) => photo.uploadId === currentUploadId);
                if (!hasMoreFromUpload) {
                  uploadQueue.shift();
                }
              }

              if (remaining > 0 && uploadQueue.length === 0) {
                // Fallback: trim oldest pending placeholders when we got new photos but no matching upload ids (e.g. pusher fired before completion hook)
                nextPending = nextPending.slice(0, Math.max(0, nextPending.length - remaining));
              }

              if (nextPending.length === 0) {
                completedUploadIdsRef.current = [];
              } else {
                completedUploadIdsRef.current = uploadQueue;
              }

              return nextPending;
            });
          }

          setAlbumPhotos((albums: any) => setAlbumAndPhotos(albums, data.length, albumId));
        } else {
          setPhotos([]);
          setPendingPhotos([]);
          completedUploadIdsRef.current = [];
        }

        setInitialLoad(false);
      }

      addGalleryBreadcrumb('getPhotos finished', {
        requestId,
        reason,
        fetched: response?.data?.length || 0,
        total: response?.meta?.total || 0,
        durationMs: (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAtMs,
      });
    } catch (err: unknown) {
      addGalleryBreadcrumb(
        'getPhotos error',
        {
          requestId,
          reason,
          error: err instanceof Error ? err.message : String(err),
          durationMs: (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAtMs,
        },
        'error'
      );
      throw err;
    } finally {
      inFlightRequestCountRef.current = Math.max(0, inFlightRequestCountRef.current - 1);
      setFetching(false);
    }
  }, [addGalleryBreadcrumb, albumId, dispatch, roomId, setAlbumPhotos, title]);

  // initial API call
  useEffect(() => {
    if (isOnScreen && initialLoad && mounted.current && photos.length === 0 && inFlightRequestCountRef.current === 0) {
      (async function fetchData() {
        await getPhotos('initial_on_screen');
      }());
    }

    return () => {
      if (!initialLoad && mounted.current && photos.length === 0) {
        mounted.current = false;
      }
      setFetching(false);
    };
  }, [getPhotos, initialLoad, isOnScreen, photos.length]);

  // paginate api results, append incoming photos to the existing array
  const onClickLastThumbnail = useCallback(async () => {
    if (!fetching) {
      setFetching(true);

      // gradually increase the page number to fetch next data set from the api
      setPageNumber((prevState: number) => prevState + 1);

      // getting remaining photos
      const response: any = await dispatch(listRoomPhotosPerAlbum(roomId, albumId, pageNumber));

      if (response?.data) {
        const { data } = response;

        // append new photos to existing photos
        setPhotos((items) => [...items, ...data]);

        setFetching(false);
      } else {
        setFetching(false);
      }
    }
  }, [pageNumber, fetching]);

  // update the counter, this is to highlight last thumbnail
  useEffect(() => {
    setInternalPhotosCount(photos.length);
  }, [photos]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    pendingPhotosRef.current = pendingPhotos;
  }, [pendingPhotos]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setPendingPhotos((prev) => {
        if (prev.length === 0) return prev;

        const now = Date.now();
        const next = prev.filter((photo) => now - photo.createdAt < PENDING_EXPIRY_MS);
        const activeUploadIds = new Set(next.map((photo) => photo.uploadId));
        completedUploadIdsRef.current = completedUploadIdsRef.current.filter((id) => activeUploadIds.has(id));
        return next;
      });
    }, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, []);

  const handleUploadStart = useCallback((files: File[], uploadId: string) => {
    if (!files || files.length === 0) return;
    const placeholders = files.map((file) => ({
      id: `pending-${generateUUID()}`,
      pending: true as const,
      fileName: file.name,
      uploadId,
      createdAt: Date.now(),
    }));
    setPendingPhotos((prev) => [...placeholders, ...prev]);
  }, []);

  const handleUploadComplete = useCallback(
    async (success: boolean, uploadId: string) => {
      if (!success) {
        setPendingPhotos((prev) => prev.filter((photo) => photo.uploadId !== uploadId));
        completedUploadIdsRef.current = completedUploadIdsRef.current.filter((id) => id !== uploadId);
        return;
      }

      completedUploadIdsRef.current = [...completedUploadIdsRef.current, uploadId];
    },
    []
  );

  // refresh photos when ImageProcessor completes processing
  useEffect(() => {
    if (!imageProcessor) return; // Only subscribe when ImageProcessor is enabled

    const channelName = `PhotoUploadingCompletedAnnouncement.Room.${roomId}`;
    const channel = pusher.subscribe(channelName);
    const handlePhotoUploadCompleted = () => {
      getPhotos('photo_upload_completed_event');
    };

    channel.bind('App\\Events\\Websockets\\PhotoUploadingCompletedAnnouncement', handlePhotoUploadCompleted);

    return () => {
      channel.unbind(
        'App\\Events\\Websockets\\PhotoUploadingCompletedAnnouncement',
        handlePhotoUploadCompleted
      );
      channel.unsubscribe(channelName);
    };
  }, [addGalleryBreadcrumb, getPhotos, pusher, roomId, albumId, imageProcessor]);

  // refresh photos when RoomContent signals processing completed
  useEffect(() => {
    if (!imageProcessor) return;
    if (!imageProcessorRefreshToken) return;

    getPhotos('image_processor_refresh_token');
  }, [imageProcessor, imageProcessorRefreshToken, getPhotos]);

  // refresh on photo delete
  useEffect(() => {
    if (refreshOnDelete) {
      (async function fetchData() {
        await getPhotos('photo_delete');
      }());
      setRefreshOnDelete(false);
      dispatch(setPhotosDeleted(false));

      // refresh and set rooms photos for photo select in select all mode
      dispatch(listLocationRoomsForPhotoSelect(location?.id));
      dispatch(refreshDataForPhotoSelection(null, location?.id));
    }
  }, [dispatch, getPhotos, location, refreshOnDelete]);

  return (
    <Gallery
      galleryInstanceId={galleryInstanceIdRef.current}
      ref={ref}
      roomId={roomId}
      albumId={albumId}
      photos={[...pendingPhotos, ...photos]}
      fetching={fetching}
      title={title}
      hide={hide}
      editable={editable}
      setRefreshOnDelete={setRefreshOnDelete}
      photosCount={getPhotosCount(photosCount, internalPhotosCount)}
      onClickLastThumbnail={onClickLastThumbnail}
      onUploadStart={handleUploadStart}
      onUploadComplete={handleUploadComplete}
    />
  );
};

GalleryContainer.defaultProps = {
  hide: false,
};

const GalleryContainerMemo = memo(GalleryContainer, areEqual);
export { GalleryContainerMemo as Gallery };
